import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  FormHelperText,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

export const acquirer_fields = [
  {
    label: "ISO",
    name: "iso",
    type: "select",
    options: ["True", "False"],
    shrink: true,
    maxLength: 3,
    required: true,
    helperText: "Belongs to ISO or not",
  },
  {
    label: "ISO ID",
    name: "isoID",
    type: "text",
    shrink: true,
    maxLength: 4,
    required: (formData) => formData.acquirer.iso === "True",
    helperText: "ISO code",
  },
  {
    label: "Email",
    name: "email",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: true,
    helperText: "Email Acquirer, also used to send user data",
  },
  {
    label: "Currency Code",
    name: "currency",
    type: "select",
    options: ["BRL", "USD", "MXN", "COP", "BOB", "VES"],
    shrink: true,
    maxLength: 5,
    required: true,
    helperText: "Currency",
  },
  {
    label: "Merchant Name",
    name: "name",
    type: "text",
    shrink: true,
    maxLength: 16,
    required: true,
    helperText: "DBA (Doing Business As) name",
  },
  // { label: "Store", name: "store", type: "text", shrink: true, maxLength: 8, required: false, helperText: "Store identifier or name" },
  {
    label: "Address",
    name: "address",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: true,
    helperText: "Street address of the merchant",
  },
  {
    label: "Merchant ID",
    name: "merchantID",
    type: "text",
    shrink: true,
    maxLength: 64,
    required: true,
    helperText: "Unique merchant identifier",
  },
  {
    label: "MCC ID",
    name: "mccID",
    type: "text",
    shrink: true,
    maxLength: 4,
    required: true,
    helperText: "Merchant Category Code (ISO18245 Compliant)",
  },
  {
    label: "City",
    name: "city",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: true,
    helperText: "",
  },
  {
    label: "State",
    name: "merchantState",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: true,
    helperText: "State, Province, Department",
  },
  {
    label: "Postal Code",
    name: "postalCode",
    type: "text",
    shrink: true,
    maxLength: 16,
    required: true,
    helperText: "ZIP or postal code",
  },
  {
    label: "Legal Name",
    name: "legal_name",
    type: "text",
    shrink: true,
    maxLength: 16,
    required: (formData) => formData.acquirer.iso === "True",
    helperText: "Official legal name of the business",
  },
  {
    label: "Business Email",
    name: "business_email",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: (formData) => formData.acquirer.iso === "True",
    helperText: "Primary contact email for the business",
  },
  {
    label: "Business Phone",
    name: "business_phone",
    type: "text",
    shrink: true,
    maxLength: 16,
    required: (formData) => formData.acquirer.iso === "True",
    helperText: "Primary business contact phone number",
  },
  {
    label: "Business Type",
    name: "business_type",
    type: "select",
    options: [
      "Sole Pro",
      "Corp.",
      "LLC",
      "Partner.",
      "Non-Pro.",
      "Gov.",
      "Other",
    ],
    shrink: true,
    maxLength: 8,
    required: (formData) => formData.acquirer.iso === "True",
    helperText:
      "Type of business (e.g., LLC, Corporation, Sole Proprietorship)",
  },
  {
    label: "Taxpayer ID",
    name: "taxpayer_id",
    type: "text",
    shrink: true,
    maxLength: 16,
    required: (formData) => formData.acquirer.iso === "True",
    helperText: "Tax identification number (e.g., EIN, CNPJ, RFC)",
  },
];

const AcquirerFields = ({
  formData,
  handleInputChange,
  acquirerData,
  submittedSteps,
  invalidFields,
}) => {
  useEffect(() => {
    if (acquirerData?.isoID) {
      handleInputChange({ target: { name: "iso", value: "True" } });
      handleInputChange({
        target: { name: "isoID", value: acquirerData.isoID },
      });
    } else {
      handleInputChange({ target: { name: "iso", value: "False" } });
    }
    if (acquirerData?.currency_code) {
      handleInputChange({
        target: { name: "currency", value: acquirerData.currency_code },
      });
    }
    handleInputChange({ target: { name: "amountOther", value: "False" } });
    handleInputChange({ target: { name: "store", value: "0001" } });
    // Merchant ID is auto-generated (UUID) on creation and hidden from the form.
    if (!formData.acquirer?.merchantID) {
      handleInputChange({
        target: { name: "merchantID", value: crypto.randomUUID() },
      });
    }
  }, [acquirerData, handleInputChange]);

  return (
    <>
      {acquirer_fields.map((field) => {
        if (field.name === "amountOther") {
          return null;
        }

        // Merchant ID is auto-generated (UUID) on creation; do not render it.
        if (field.name === "merchantID") {
          return null;
        }

        const isRequired =
          typeof field.required === "function"
            ? field.required(formData)
            : field.required;
        const fieldId = `acquirer.${field.name}`;
        const isError =
          invalidFields?.includes(fieldId) ||
          (submittedSteps.has("acquirer") &&
            isRequired &&
            !formData.acquirer[field.name]);

        if (field.name === "isoID" && formData.acquirer.iso !== "True") {
          return null;
        }

        return (
          <Grid item xs={12} sm={3} key={field.name}>
            {field.type === "select" ? (
              <FormControl
                fullWidth
                required={isRequired}
                disabled={
                  field.name === "iso" ||
                  (field.name === "currency" && !!acquirerData?.currency_code)
                }
                error={isError}
              >
                <InputLabel
                  id={`${field.name}-label`}
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                      fontSize: "1.2rem",
                    },
                  }}
                >
                  {field.label}
                  {field.helperText && (
                    <Tooltip title={field.helperText} arrow>
                      <IconButton
                        size="small"
                        sx={{ padding: 0, marginLeft: "8px" }}
                      >
                        <InfoIcon color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputLabel>
                <Select
                  labelId={`${field.name}-label`}
                  name={field.name}
                  label={field.label}
                  value={formData.acquirer[field.name] || ""}
                  onChange={handleInputChange}
                  disabled={
                    field.name === "iso" ||
                    (field.name === "currency" && !!acquirerData?.currency_code)
                  }
                >
                  {field.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {isError && (
                  <FormHelperText error>This field is required</FormHelperText>
                )}
              </FormControl>
            ) : (
              <TextField
                label={
                  <>
                    {field.label}
                    {field.helperText && (
                      <Tooltip title={field.helperText} arrow>
                        <IconButton
                          size="small"
                          sx={{ padding: 0, marginLeft: "4px" }}
                        >
                          <InfoIcon color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                }
                name={field.name}
                value={formData.acquirer[field.name] || ""}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: field.shrink,
                  sx: {
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                      fontSize: "1.2rem",
                    },
                  },
                }}
                inputProps={{ maxLength: field.maxLength }}
                required={isRequired}
                error={isError}
                helperText={isError ? "This field is required" : ""}
                fullWidth
                disabled={field.name === "isoID" && !!acquirerData?.isoID}
              />
            )}
          </Grid>
        );
      })}
    </>
  );
};

export default AcquirerFields;
